import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";

export const OVERDUE_INVOICE_CONCENTRATION_COLUMNS = [
  { key: "invoice_number", label: "Invoice No." },
  { key: "currency", label: "Currency" },
  { key: "due_date", label: "Due Date", type: "date" },
  { key: "days_overdue", label: "Overdue Days", type: "pill" },
  { key: "invoice_total", label: "Invoice Total", mono: true },
  { key: "paid", label: "Paid", mono: true },
  { key: "offset", label: "Offset", mono: true },
  { key: "outstanding", label: "Outstanding", mono: true },
];

function daysOverdueTone(days) {
  if (!Number.isFinite(days) || days === 0) return "green";
  // if (days <= 90) return "amber";
  return "red";
}

function formatOverdueAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function buildOverdueInvoiceConcentrationRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    const days = typeof item?.days_overdue === "number" ? item.days_overdue : parseFloat(item?.days_overdue);
    const safeDays = Number.isFinite(days) ? days : 0;
    return {
      id: item?.invoice_slug || item?.invoice_number || index,
      invoice_number: item?.invoice_number || "-",
      currency: item?.currency || "-",
      due_date: item?.due_date,
      days_overdue: { label: `${safeDays} days`, tone: daysOverdueTone(safeDays) },
      invoice_total: formatOverdueAmount(item?.invoice_total, item?.currency),
      paid: formatOverdueAmount(item?.paid, item?.currency),
      offset: formatOverdueAmount(item?.offset, item?.currency),
      outstanding: formatOverdueAmount(item?.outstanding, item?.currency),
      _lessee: item?.lessee || "Unknown",
    };
  });
}

// Drives the "Overdue Invoice Concentration" popup: clicking a pie slice/legend
// entry for a lessee filters invoice_breakdown down to that lessee's invoices.
export function useOverdueInvoiceConcentrationBreakdown({ invoiceBreakdown, onTrack }) {
  const [selectedLessee, setSelectedLessee] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedLessee(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildOverdueInvoiceConcentrationRows(invoiceBreakdown), [invoiceBreakdown]);
  const canRender = allRows.length > 0;

  const rows = useMemo(() => {
    if (!selectedLessee) return allRows;
    return allRows.filter((row) => row._lessee === selectedLessee);
  }, [allRows, selectedLessee]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series") return;
      const lessee = params.name || null;
      setSelectedLessee(lessee);
      setModalOpen(true);
      onTrack?.(`Graph Slice Clicked: ${lessee || ""}`);
    },
    [onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(() => selectedLessee || "", [selectedLessee]);

  return { rows, canRender, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function OverdueInvoiceConcentrationModal({ open, onClose, rows, titleSuffix }) {
  const title = "Overdue Invoice Concentration";
  const invoiceCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${invoiceCount} ${invoiceCount === 1 ? "Invoice" : "Invoices"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={OVERDUE_INVOICE_CONCENTRATION_COLUMNS}
      rows={rows}
    />
  );
}
